import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import {
  Target, ClipboardList, Lock,
  Bell, ChevronDown, Check, Trash2,
  FileText,
} from "lucide-react";
import { EditableValue, AddItemForm, RowActionButton } from "./_editable";
import { CategorySheet } from "./CategorySheet";
import {
  // data + nav
  KPIS, CASH_FLOW, BUDGET_SLICES_DEF, BUDGET_DEMO, buildBudgetSlices,
  ROADMAP, REMINDERS, TOP_GOALS, CATEGORY_CARDS, SHIFTS, HX_INVESTMENTS,
  NAV_PUBLIC, NAV_PRIVATE,
  // weekly schedule
  SHIFT_TYPES, SHIFT_BY_ID, DAYS_WEEK, DEMO_WEEK,
  // command row
  COMMAND_CARDS, deriveCommandValue, formatPtDate, CURRENT_MONTH_LABEL,
  // calendar
  DOW_MAP, PT_DOW_SHORT, PT_MONTHS_SHORT, PT_MONTHS_FULL, WEEK_START_ORDER, toIso, isSameDay,
  // legacy
  LEGACY_TEMPLATES,
  // mission
  MISSION_BREAKDOWN,
  // sheets & views
  SHEET_MAP, ausbildungSheet, ALL_PRIVATE_VIEWS,
  // builders + visuals + format
  COLORS_LIST, SHIFT_VISUALS, fmtEuro,
  buildKpiList, buildCategoryCards, buildReminders, buildGoals, buildInvestments, buildShifts,
} from "./_finance_data";
import {
  Home, BookOpen, LayoutGrid, ChartArea, Map, Info,
  ShieldCheck, Heart, GraduationCap, Car, Plane, Briefcase,
  Calendar, BarChart3, Settings,
  Wallet, Receipt, PiggyBank, TrendingUp,
  Sun, Sunrise, Sunset, Moon, Circle,
  Flag, Star, Trophy, Sparkles, Users, Globe,
} from "lucide-react";

/* =================== SUB-COMPONENTS =================== */

const Sidebar = React.memo(function Sidebar({ active, setActive, unlocked }) {
  return (
  <aside className="hidden lg:flex flex-col w-[260px] shrink-0 border-r border-white/[0.10] bg-black/40 backdrop-blur-xl sticky top-0 h-screen overflow-y-auto" data-testid="finance-sidebar">
    {/* Brand */}
    <div className="px-5 py-6 border-b border-white/[0.10]">
      <Link to="/" className="flex items-center gap-2.5 group">
        <div className="w-9 h-9 rounded-md hx-metal flex items-center justify-center">
          <span className="font-display font-bold text-hx-neon text-[11px]">HX</span>
        </div>
        <div className="leading-tight">
          <div className="font-display font-semibold text-[11px] tracking-tight text-hx-text">HX ECOSYSTEMS</div>
        </div>
      </Link>
      <div className="mt-5 rounded-md border border-hx-neon/30 bg-hx-neon/[0.06] p-3" style={{ boxShadow: "0 0 18px -4px rgba(10,155,95,0.3) inset" }}>
        <div className="font-display font-semibold text-[10px] uppercase tracking-[0.22em] text-hx-neon">HX Finance Command</div>
        <div className="mt-1 text-[10px] text-hx-muted leading-tight">Cada Euro Tem uma Missão.</div>
      </div>
    </div>

    {/* Public nav */}
    <div className="px-3 py-4">
      <div className="px-2 text-[9px] uppercase tracking-[0.24em] text-hx-muted/70 mb-2">Público</div>
      <ul className="space-y-0.5">
        {NAV_PUBLIC.map((n) => (
          <li key={n.id}>
            <button
              onClick={() => setActive(n.id)}
              data-testid={`finance-nav-${n.id}`}
              className={`w-full text-left flex items-center gap-2.5 px-2 py-1.5 rounded text-[12px] transition-colors ${
                active === n.id ? "text-hx-neon bg-hx-neon/[0.06]" : "text-hx-muted hover:text-hx-text hover:bg-white/[0.03]"
              }`}
            >
              <n.Icon size={13} strokeWidth={1.6} />
              {n.label}
            </button>
          </li>
        ))}
      </ul>
    </div>

    {/* Private nav */}
    <div className="px-3 py-3 border-t border-white/[0.10]">
      <div className="px-2 text-[9px] uppercase tracking-[0.24em] text-hx-muted/70 mb-2 flex items-center gap-1.5">
        Privado {!unlocked && <Lock size={9} strokeWidth={2} />}
        {!unlocked && <span className="text-[8px] tracking-[0.18em]">(login necessário)</span>}
      </div>
      <ul className="space-y-0.5">
        {NAV_PRIVATE.map((n) => (
          <li key={n.id}>
            <button
              onClick={() => setActive(n.id)}
              disabled={!unlocked}
              data-testid={`finance-nav-${n.id}`}
              className={`w-full text-left flex items-center gap-2.5 px-2 py-1.5 rounded text-[12px] transition-colors ${
                !unlocked ? "text-hx-muted/50 cursor-not-allowed" :
                active === n.id ? "text-hx-neon bg-hx-neon/[0.06]" : "text-hx-muted hover:text-hx-text hover:bg-white/[0.03]"
              }`}
            >
              {unlocked ? <n.Icon size={13} strokeWidth={1.6} /> : <Lock size={11} strokeWidth={1.8} />}
              {n.label}
            </button>
          </li>
        ))}
      </ul>
    </div>

    {/* Mantra */}
    <div className="mt-auto px-5 py-5 border-t border-white/[0.10]">
      <div className="rounded-md border border-hx-neon/20 bg-hx-neon/[0.04] p-3 relative">
        <span className="absolute -top-1.5 left-3 text-hx-neon text-lg leading-none">"</span>
        <ul className="text-[11px] text-hx-text/85 leading-relaxed font-medium space-y-0.5">
          <li>Honra a Fé.</li>
          <li>Protege a Família.</li>
          <li>Constrói Segurança.</li>
          <li>Cria Experiências.</li>
          <li>Desenvolve Capacidades.</li>
          <li>Faz crescer HX Ecosystems.</li>
        </ul>
        <span className="absolute -bottom-3 right-3 text-hx-neon text-lg leading-none">"</span>
      </div>
      <div className="mt-4 text-[9px] uppercase tracking-[0.22em] text-hx-muted/60">© HX Ecosystems {new Date().getFullYear()}</div>
    </div>
  </aside>
  );
});

const TopHeader = React.memo(function TopHeader({ unlocked, onUnlockClick, onLock }) {
  return (
  <header className="relative px-5 lg:px-8 py-5 lg:py-6 border-b border-white/[0.10] bg-black/30">
    <div className="flex items-start lg:items-center justify-between gap-4 flex-wrap">
      <div>
        <h1 className="font-display font-bold text-2xl lg:text-3xl tracking-tight text-hx-text">HX FINANCE COMMAND</h1>
        <p className="mt-1 text-[12px] lg:text-[13px] text-hx-muted">A tua Vida. A tua Família. A tua Missão. O teu Legado.</p>
      </div>
      <div className="flex items-center gap-3">
        {unlocked ? (
          <button
            onClick={onLock}
            data-testid="finance-mode-badge"
            className="rounded-md border border-hx-neon/40 bg-hx-neon/[0.08] px-3.5 py-2 flex items-center gap-2"
            style={{ boxShadow: "0 0 18px -4px rgba(10,155,95,0.4)" }}
          >
            <Lock size={12} className="text-hx-neon" strokeWidth={2} />
            <div className="text-left leading-tight">
              <div className="text-[10px] uppercase tracking-[0.2em] text-hx-neon font-semibold">Modo Privado</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">Área Segura</div>
            </div>
          </button>
        ) : (
          <button
            onClick={onUnlockClick}
            data-testid="finance-mode-badge"
            className="rounded-md border border-white/[0.10] bg-white/[0.03] hover:border-hx-neon/40 hover:bg-hx-neon/[0.06] px-3.5 py-2 flex items-center gap-2 transition-colors"
          >
            <Lock size={12} className="text-hx-muted" strokeWidth={2} />
            <div className="text-left leading-tight">
              <div className="text-[10px] uppercase tracking-[0.2em] text-hx-muted font-semibold">Modo Público</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-hx-muted/70">Clique para desbloquear</div>
            </div>
          </button>
        )}
        <button className="w-9 h-9 rounded-md border border-white/[0.10] bg-white/[0.03] flex items-center justify-center text-hx-muted hover:text-hx-text transition-colors relative" aria-label="Notificações">
          <Bell size={14} strokeWidth={1.7} />
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-hx-neon" />
        </button>
        <button className="flex items-center gap-2.5 rounded-md border border-white/[0.10] bg-white/[0.03] px-2.5 py-1.5 hover:border-hx-neon/30 transition-colors">
          <div className="w-7 h-7 rounded-full hx-metal flex items-center justify-center">
            <span className="font-display font-bold text-hx-neon text-[10px]">HX</span>
          </div>
          <div className="text-left leading-tight hidden sm:block">
            <div className="text-[11px] font-semibold text-hx-text">HX Commander</div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">Líder Familiar & Projetos</div>
          </div>
          <ChevronDown size={11} className="text-hx-muted" />
        </button>
      </div>
    </div>
  </header>
  );
});

const KPICard = React.memo(function KPICard({ kpi, onSave, editable }) {
  const isRed = kpi.accent === "red";
  return (
    <div
      className="rounded-lg border border-white/[0.10] p-4 lg:p-5 relative overflow-hidden"
      style={{
        background: "linear-gradient(180deg, rgba(20,26,34,0.7) 0%, rgba(10,15,26,0.90) 100%)",
        boxShadow: isRed
          ? "0 0 0 1px rgba(239,68,68,0.08) inset, 0 12px 28px -20px rgba(0,0,0,0.6)"
          : "0 0 0 1px rgba(10,155,95,0.08) inset, 0 12px 28px -20px rgba(0,0,0,0.6)",
      }}
      data-testid={`fc-kpi-${kpi.label.toLowerCase().replace(/\W+/g,"-")}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted leading-tight font-medium">{kpi.label}</div>
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${isRed ? "bg-red-500/[0.08] border border-red-500/30" : "hx-metal"}`}>
          <kpi.Icon size={12} className={isRed ? "text-red-300" : "text-hx-neon"} strokeWidth={1.7} />
        </div>
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        {editable && onSave ? (
          <EditableValue
            value={kpi.rawValue}
            onSave={onSave}
            inputClassName={`font-display font-bold text-2xl lg:text-3xl tabular-nums tracking-tight ${isRed ? "text-red-300" : "text-hx-neon"}`}
            testId={`fc-kpi-edit-${kpi.label.toLowerCase().replace(/\W+/g,"-")}`}
          />
        ) : (
          <span className={`font-display font-bold text-2xl lg:text-3xl tabular-nums tracking-tight ${isRed ? "text-red-300" : "text-hx-neon"}`}
                style={isRed ? undefined : { textShadow: "0 0 18px rgba(10,155,95,0.35)" }}>
            {kpi.value}
          </span>
        )}
        <span className="text-[12px] text-hx-muted font-medium">{kpi.unit}</span>
      </div>
      <div className="mt-1 text-[10px] text-hx-muted">{kpi.sub}</div>
      <div className={`mt-3 text-[10px] uppercase tracking-[0.18em] font-semibold ${
        isRed ? "text-amber-300/90" : "text-hx-neon"
      }`}>
        {kpi.delta}
      </div>
    </div>
  );
});

const CashFlowChart = React.memo(function CashFlowChart() {
  return (
  <div className="rounded-lg border border-white/[0.10] p-5" style={{ background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)" }} data-testid="fc-cashflow">
    <div className="flex items-center justify-between mb-1">
      <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-semibold">Fluxo de Caixa</div>
      <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.16em] text-hx-muted">
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-emerald-400" /> Rendimento</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-red-400" /> Despesa</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-blue-400" /> Poupança</span>
      </div>
    </div>
    <div className="h-[280px] mt-3">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={CASH_FLOW} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
          <XAxis dataKey="m" tick={{ fill: "rgba(152,162,179,0.6)", fontSize: 11, fontFamily: "monospace" }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.05)" }} />
          <YAxis tick={{ fill: "rgba(152,162,179,0.5)", fontSize: 10, fontFamily: "monospace" }} tickLine={false} axisLine={false} domain={[0, 3500]} ticks={[0, 750, 1500, 2250, 3000]} />
          <Tooltip
            contentStyle={{ background: "rgba(10,15,26,0.90)", border: "1px solid rgba(10,155,95,0.2)", borderRadius: 6, fontSize: 11 }}
            labelStyle={{ color: "#0A9B5F", textTransform: "uppercase", letterSpacing: 1, fontSize: 10 }}
            formatter={(v) => fmtEuro(v)}
          />
          <Line type="monotone" dataKey="income"   stroke="#34D399" strokeWidth={2} dot={{ r: 3, fill: "#34D399" }} activeDot={{ r: 5 }} />
          <Line type="monotone" dataKey="expenses" stroke="#F87171" strokeWidth={2} dot={{ r: 3, fill: "#F87171" }} activeDot={{ r: 5 }} />
          <Line type="monotone" dataKey="savings"  stroke="#60A5FA" strokeWidth={2} dot={{ r: 3, fill: "#60A5FA" }} activeDot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
  );
});

const BudgetDonut = ({ slices, totalLabel = "Despesa Total" }) => {
  const total = slices.reduce((s, x) => s + x.v, 0);
  return (
    <div className="rounded-lg border border-white/[0.10] p-5" style={{ background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)" }} data-testid="fc-budget">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-semibold">Alocação do Orçamento</div>
        <span className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">Auto-sincronizado</span>
      </div>
      <div className="mt-3 grid grid-cols-[160px_1fr] gap-5 items-center">
        <div className="relative h-[160px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={slices} dataKey="v" nameKey="k" innerRadius={48} outerRadius={70} stroke="rgba(10,15,26,0.90)" strokeWidth={2}>
                {slices.map((b, i) => <Cell key={i} fill={b.color} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="font-display font-bold text-xl text-hx-text tabular-nums">{fmtEuro(total)}</div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">{totalLabel}</div>
          </div>
        </div>
        <ul className="space-y-1.5">
          {slices.map((b) => (
            <li key={b.k} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 text-[12px]">
              <span className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: b.color }} />
                <span className="text-hx-text truncate">{b.k}</span>
              </span>
              <span className="font-mono text-hx-muted tabular-nums text-[11px]">{total > 0 ? Math.round((b.v / total) * 100) : 0}%</span>
              <span className="font-mono text-hx-text tabular-nums text-[11px]">{fmtEuro(b.v)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const colorMap = {
    "Sempre":              "text-hx-neon bg-hx-neon/10 border-hx-neon/30",
    "Contínuo":            "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    "Em Curso":            "text-blue-300 bg-blue-500/10 border-blue-500/30",
    "Próxima Prioridade":  "text-amber-300 bg-amber-500/10 border-amber-500/30",
  };
  return <span className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-px rounded-sm border ${colorMap[status] || "text-hx-muted border-white/[0.10]"}`}>{status}</span>;
};

const RoadmapPanel = ({ steps, onToggleDone, onSetNext, editable }) => {
  return (
    <div className="rounded-lg border border-white/[0.10] p-5" style={{ background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)" }} data-testid="fc-roadmap">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-semibold">Roadmap Pessoal</div>
        <span className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">{editable ? "Clica para alternar" : "(Privado)"}</span>
      </div>
      <ol className="space-y-2.5">
        {steps.map((r) => (
          <li key={r.n} className="flex items-center gap-3 group" data-testid={`fc-roadmap-${r.n}`}>
            <button
              onClick={() => editable && onToggleDone(r.n)}
              disabled={!editable}
              data-testid={`fc-roadmap-toggle-${r.n}`}
              className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 border transition-all ${
                r.done ? "bg-hx-neon/20 border-hx-neon text-hx-neon" :
                r.next ? "bg-amber-500/20 border-amber-500 text-amber-300" :
                "bg-white/[0.03] border-white/[0.10] text-hx-muted"
              } ${editable ? "hover:scale-110 cursor-pointer" : "cursor-default"}`}
              title={editable ? (r.done ? "Marcar como em curso" : "Marcar como concluído") : ""}
            >
              {r.n}
            </button>
            <div className="flex-1 min-w-0">
              <div className={`text-[12px] font-medium ${r.done ? "text-hx-text" : "text-hx-muted"}`}>{r.n} {r.label}</div>
              <div className="text-[10px] text-hx-muted/80">{r.status || ""}</div>
            </div>
            {r.done ? (
              <Check size={13} className="text-hx-neon" strokeWidth={2.4} />
            ) : (
              <button
                onClick={() => editable && onSetNext(r.n)}
                disabled={!editable}
                data-testid={`fc-roadmap-setnext-${r.n}`}
                className={`shrink-0 ${editable ? "hover:scale-125 transition-transform" : ""}`}
                title={editable ? "Definir como próxima prioridade" : ""}
              >
                <Circle size={11} className={r.next ? "text-amber-400" : "text-hx-muted/40"} strokeWidth={r.next ? 2 : 1.5} />
              </button>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

const RemindersPanel = ({ items, onAdd, onDelete, editable }) => (
  <div className="rounded-lg border border-white/[0.10] p-5" style={{ background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)" }} data-testid="fc-reminders">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-semibold">Lembretes Inteligentes</div>
      <span className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">(Privado)</span>
    </div>
    <ul className="space-y-2">
      <AnimatePresence initial={false}>
        {items.map((r, i) => (
          <motion.li
            key={r.id || i}
            layout
            initial={{ opacity: 0, x: 4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -4 }}
            className="flex items-center gap-3 text-[12px] group"
          >
            <Calendar size={11} style={{ color: r.color || "#0A9B5F" }} strokeWidth={1.8} />
            <span className="flex-1 truncate text-hx-text/90">{r.label}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-hx-muted tabular-nums shrink-0">{r.date || (r.due_date || "")}</span>
            {editable && r.id && (
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                <RowActionButton Icon={Trash2} danger onClick={() => onDelete(r.id)} title="Eliminar" testId={`fc-reminder-del-${r.id}`} />
              </span>
            )}
          </motion.li>
        ))}
      </AnimatePresence>
    </ul>
    {editable && (
      <AddItemForm
        addLabel="Adicionar lembrete"
        testId="fc-reminder-add"
        fields={[
          { key: "label",    label: "Título",   required: true, placeholder: "ex: Exame de Alemão" },
          { key: "due_date", label: "Data",     type: "date", required: true },
        ]}
        onSubmit={onAdd}
      />
    )}
  </div>
);

const TopGoalsPanel = ({ items, onAdd, onUpdate, onDelete, editable }) => (
  <div className="rounded-lg border border-white/[0.10] p-5" style={{ background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)" }} data-testid="fc-topgoals">
    <div className="flex items-center justify-between mb-4">
      <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-semibold">Objetivos Principais</div>
      <span className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">(Privado)</span>
    </div>
    <ul className="space-y-3.5">
      <AnimatePresence initial={false}>
        {items.map((g, i) => (
          <motion.li key={g.id || i} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid={`fc-goal-${i}`} className="group">
            <div className="flex items-center justify-between text-[11px] gap-2">
              <span className="text-hx-text truncate">{g.label}</span>
              <span className="flex items-center gap-1.5">
                {editable && g.id ? (
                  <EditableValue
                    value={g.pct}
                    onSave={(v) => onUpdate(g.id, { progress: v })}
                    inputClassName="font-mono text-hx-neon tabular-nums text-[11px]"
                    testId={`fc-goal-edit-${g.id}`}
                  />
                ) : (
                  <span className="font-mono text-hx-neon tabular-nums">{g.pct}%</span>
                )}
                {editable && !editable && null}
                {editable && g.id && (
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <RowActionButton Icon={Trash2} danger onClick={() => onDelete(g.id)} title="Eliminar" testId={`fc-goal-del-${g.id}`} />
                  </span>
                )}
              </span>
            </div>
            <div className="mt-1 h-1 rounded-full bg-white/[0.03] overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-hx-neon/70 to-hx-neon" style={{ width: `${g.pct}%`, boxShadow: "0 0 8px rgba(10,155,95,0.5)" }} />
            </div>
          </motion.li>
        ))}
      </AnimatePresence>
    </ul>
    {editable && (
      <AddItemForm
        addLabel="Adicionar objetivo"
        testId="fc-goal-add"
        fields={[
          { key: "label",    label: "Objetivo", required: true, placeholder: "ex: Comprar bicicleta" },
          { key: "progress", label: "Progresso (%)", type: "number", placeholder: "0-100" },
        ]}
        onSubmit={onAdd}
      />
    )}
    {!editable && (
      <button className="mt-5 w-full text-[10px] uppercase tracking-[0.18em] text-hx-neon hover:text-hx-text transition-colors flex items-center justify-center gap-1.5 py-1.5 border-t border-white/[0.10] pt-3.5">
        Ver Todos os Objetivos & Marcos →
      </button>
    )}
  </div>
);

const CategoryCard = ({ c, onSave, editable }) => (
  <div
    className="rounded-lg border border-white/[0.10] p-4 lg:p-5 flex flex-col"
    style={{ background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)" }}
    data-testid={`fc-cat-${c.key}`}
  >
    <div className="text-[10px] uppercase tracking-[0.22em] text-hx-neon font-semibold text-center">{c.label}</div>
    <div className="my-4 flex justify-center">
      <div className="w-14 h-14 rounded-lg flex items-center justify-center" style={{ background: `${c.accent}18`, border: `1px solid ${c.accent}40` }}>
        <c.Icon size={24} style={{ color: c.accent }} strokeWidth={1.6} />
      </div>
    </div>
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-[0.18em] text-hx-muted">{c.sub}</div>
      <div className="mt-1.5 font-display font-bold text-2xl tabular-nums text-hx-text">
        {editable && onSave ? (
          <EditableValue
            value={c.rawValue}
            onSave={onSave}
            inputClassName="font-display font-bold text-2xl tabular-nums text-hx-text"
            testId={`fc-cat-edit-${c.key}`}
          />
        ) : c.value}
        {" "}<span className="text-[12px] text-hx-muted">{c.unit}</span>
      </div>
      <div className="mt-1 text-[10px] text-hx-muted">{c.note}</div>
    </div>
    <div className="mt-auto pt-4 h-1 rounded-full bg-white/[0.03] overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${c.pct}%`, background: c.accent, boxShadow: `0 0 6px ${c.accent}` }} />
    </div>
  </div>
);

const WorkShiftPlanning = ({ items, onAdd, onDelete, editable }) => {
  const totalHours = items.reduce((s, x) => s + (x.hours || 0), 0);
  const totalIncome = items.reduce((s, x) => s + (x.income || 0), 0);
  return (
    <div className="rounded-lg border border-white/[0.10] p-5" style={{ background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)" }} data-testid="fc-shifts">
      <div className="flex items-center justify-between mb-5">
        <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-semibold">Planeamento de Turnos</div>
        <span className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">(Esta Semana)</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <AnimatePresence initial={false}>
          {items.map((s, i) => (
            <motion.div key={s.id || i} layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} className="rounded-md border border-white/[0.10] p-3 text-center bg-black/30 group relative" data-testid={`fc-shift-${i}`}>
              <s.Icon size={20} style={{ color: s.color || "#0A9B5F" }} className="mx-auto" strokeWidth={1.5} />
              <div className="mt-2 text-[11px] font-semibold text-hx-text">{s.label}</div>
              <div className="text-[9px] uppercase tracking-[0.16em] text-hx-muted mt-0.5">{s.range}</div>
              <div className="mt-3 text-[11px] text-hx-text"><span className="font-bold tabular-nums">{s.count}</span> Turnos</div>
              <div className="text-[11px] text-hx-muted tabular-nums">{s.hours}h</div>
              <div className="mt-2 font-display font-bold text-lg tabular-nums" style={{ color: s.color || "#0A9B5F" }}>{s.income} €</div>
              <div className="mt-2 h-0.5 rounded-full" style={{ background: s.color || "#0A9B5F", opacity: 0.5 }} />
              {editable && s.id && (
                <button onClick={() => onDelete(s.id)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 rounded text-hx-muted hover:text-red-300 hover:bg-red-500/10 transition-all" data-testid={`fc-shift-del-${s.id}`} title="Eliminar">
                  <Trash2 size={10} strokeWidth={2} />
                </button>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        <div className="rounded-md border border-hx-neon/30 bg-hx-neon/[0.06] p-3 text-center" style={{ boxShadow: "0 0 18px -4px rgba(10,155,95,0.3)" }}>
          <div className="text-[9px] uppercase tracking-[0.18em] text-hx-neon font-bold">Total Esta Semana</div>
          <div className="mt-3 font-display font-bold text-2xl text-hx-text tabular-nums">{totalHours}h</div>
          <div className="text-[9px] uppercase tracking-[0.16em] text-hx-muted">Horas Planeadas</div>
          <div className="mt-3 font-display font-bold text-xl text-hx-neon tabular-nums">{totalIncome} €</div>
          <div className="text-[9px] uppercase tracking-[0.16em] text-hx-muted">Receita Estimada</div>
        </div>
      </div>
      {editable && (
        <AddItemForm
          addLabel="Adicionar turno"
          testId="fc-shift-add"
          fields={[
            { key: "label",   label: "Nome",        required: true, placeholder: "ex: Turno Manhã" },
            { key: "range",   label: "Horário",     placeholder: "06:00 - 14:00" },
            { key: "count",   label: "Nº Turnos",   type: "number", placeholder: "3" },
            { key: "hours",   label: "Horas",       type: "number", placeholder: "24" },
            { key: "income",  label: "Receita (€)", type: "number", placeholder: "480" },
          ]}
          onSubmit={onAdd}
        />
      )}
    </div>
  );
};

const HXInvestmentsPanel = ({ items, onAdd, onUpdate, onDelete, editable }) => {
  const total = items.reduce((s, x) => s + (x.v || 0), 0);
  return (
    <div className="rounded-lg border border-white/[0.10] p-5" style={{ background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)" }} data-testid="fc-hx-investments">
      <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-semibold mb-4">Investimentos HX Ecosystems</div>
      <ul className="space-y-2.5">
        <AnimatePresence initial={false}>
          {items.map((it, i) => (
            <motion.li key={it.id || i} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid grid-cols-[1fr_auto_minmax(120px,200px)_auto_auto] items-center gap-3 text-[12px] group">
              <span className="text-hx-text truncate">{it.label}</span>
              {editable && it.id ? (
                <EditableValue value={it.v} onSave={(v) => onUpdate(it.id, { amount: v })} suffix="€" inputClassName="font-mono text-hx-muted tabular-nums text-[11px]" testId={`fc-hx-edit-v-${it.id}`} />
              ) : (
                <span className="font-mono text-hx-muted tabular-nums text-[11px]">{it.v} €</span>
              )}
              <div className="h-1 rounded-full bg-white/[0.03] overflow-hidden">
                <div className="h-full rounded-full bg-hx-neon" style={{ width: `${it.p}%`, boxShadow: "0 0 6px rgba(10,155,95,0.6)" }} />
              </div>
              {editable && it.id ? (
                <EditableValue value={it.p} onSave={(v) => onUpdate(it.id, { progress: v })} suffix="%" inputClassName="font-mono text-hx-neon tabular-nums text-[11px]" testId={`fc-hx-edit-p-${it.id}`} />
              ) : (
                <span className="font-mono text-hx-neon tabular-nums text-[11px]">{it.p}%</span>
              )}
              {editable && it.id && (
                <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <RowActionButton Icon={Trash2} danger onClick={() => onDelete(it.id)} title="Eliminar" testId={`fc-hx-del-${it.id}`} />
                </span>
              )}
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
      {editable && (
        <AddItemForm
          addLabel="Adicionar investimento"
          testId="fc-hx-add"
          fields={[
            { key: "label",    label: "Projeto", required: true, placeholder: "ex: HX Smart Point" },
            { key: "amount",   label: "Valor (€)", type: "number", placeholder: "500" },
            { key: "progress", label: "Progresso (%)", type: "number", placeholder: "60" },
          ]}
          onSubmit={onAdd}
        />
      )}
      <div className="mt-5 pt-3 border-t border-white/[0.10] flex items-center justify-between">
        <span className="text-[11px] text-hx-text font-semibold">Total Este Mês</span>
        <span className="font-display font-bold text-lg text-hx-neon tabular-nums">{fmtEuro(total)}</span>
      </div>
    </div>
  );
};

/* =================== WEEKLY SHIFT SCHEDULE (Seg → Dom) =================== */

const WeeklyShiftSchedule = ({ unlocked, weekEntries, onSetDay }) => {
  // Build current schedule map: { mon: "morning", tue: "day", ... }
  const schedule = React.useMemo(() => {
    if (!unlocked) return DEMO_WEEK;
    const m = {};
    for (const e of (weekEntries || [])) {
      if (e.label?.startsWith("week:")) {
        const dayId = e.label.slice(5);
        if (e.note && SHIFT_BY_ID[e.note]) m[dayId] = e.note;
      }
    }
    return m;
  }, [unlocked, weekEntries]);

  const totals = React.useMemo(() => {
    let hours = 0, income = 0;
    const counts = { morning: 0, day: 0, evening: 0, night: 0, off: 0 };
    for (const d of DAYS_WEEK) {
      const sid = schedule[d.id] || "off";
      const st = SHIFT_BY_ID[sid];
      hours += st.hours; income += st.income;
      counts[sid] = (counts[sid] || 0) + 1;
    }
    return { hours, income, counts };
  }, [schedule]);

  return (
    <div
      className="rounded-lg border border-white/[0.10] p-5"
      style={{ background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)" }}
      data-testid="fc-week-schedule"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-semibold">Escala Semanal</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-hx-muted mt-0.5">Segunda → Domingo · Controlo Pessoal</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap text-[10px] uppercase tracking-[0.16em] text-hx-muted">
          {SHIFT_TYPES.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: s.color, opacity: s.id === "off" ? 0.5 : 1 }} />
              {s.label}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
        {DAYS_WEEK.map((d) => {
          const sid = schedule[d.id] || "off";
          const st = SHIFT_BY_ID[sid];
          return (
            <div
              key={d.id}
              data-testid={`fc-week-day-${d.id}`}
              className="rounded-md border border-white/[0.10] bg-black/30 p-3 flex flex-col"
              style={{ boxShadow: sid !== "off" ? `inset 0 -2px 0 0 ${st.color}` : undefined }}
            >
              <div className="flex items-center justify-between">
                <span className="font-display font-bold text-[12px] text-hx-text uppercase tracking-[0.16em]">{d.short}</span>
                <st.Icon size={13} style={{ color: st.color, opacity: sid === "off" ? 0.5 : 1 }} strokeWidth={1.6} />
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.12em] font-semibold" style={{ color: st.color, opacity: sid === "off" ? 0.6 : 1 }}>
                {st.label}
              </div>
              <div className="text-[9px] font-mono text-hx-muted/80 mt-0.5">{st.range}</div>
              <div className="mt-2 flex items-baseline justify-between gap-1.5">
                <span className="font-mono text-[10px] text-hx-muted tabular-nums">{st.hours}h</span>
                <span className="font-display font-bold text-[13px] tabular-nums" style={{ color: sid === "off" ? "#6B7280" : "#0A9B5F" }}>
                  {st.income} €
                </span>
              </div>

              {unlocked ? (
                <div className="mt-3 grid grid-cols-5 gap-1">
                  {SHIFT_TYPES.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => onSetDay(d.id, opt.id)}
                      data-testid={`fc-week-set-${d.id}-${opt.id}`}
                      title={opt.label}
                      aria-label={`${d.long} — ${opt.label}`}
                      className={`h-5 rounded-sm border transition-all ${sid === opt.id ? "scale-105" : "opacity-50 hover:opacity-100"}`}
                      style={{
                        borderColor: sid === opt.id ? opt.color : "rgba(255,255,255,0.08)",
                        background: sid === opt.id ? `${opt.color}33` : "transparent",
                        boxShadow: sid === opt.id ? `0 0 8px ${opt.color}66` : undefined,
                      }}
                    >
                      <span className="block w-full h-full rounded-sm" style={{ background: opt.color, opacity: sid === opt.id ? 0.85 : 0.35 }} />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-3 h-5 flex items-center justify-center text-[9px] uppercase tracking-[0.2em] text-hx-muted/60">
                  <Lock size={9} strokeWidth={2} className="mr-1" /> Demo
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Week summary */}
      <div className="mt-5 pt-4 border-t border-white/[0.10] grid grid-cols-2 lg:grid-cols-5 gap-3">
        {SHIFT_TYPES.slice(0, 4).map((s) => (
          <div key={s.id} className="rounded-md border border-white/[0.10] bg-black/30 px-3 py-2.5 flex items-center gap-2.5">
            <s.Icon size={14} style={{ color: s.color }} strokeWidth={1.6} />
            <div className="flex-1 min-w-0">
              <div className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">{s.label}</div>
              <div className="font-mono text-[12px] text-hx-text tabular-nums">
                <span className="font-bold">{totals.counts[s.id] || 0}</span>
                <span className="text-hx-muted"> × {s.hours}h = {((totals.counts[s.id] || 0) * s.income)} €</span>
              </div>
            </div>
          </div>
        ))}
        <div
          className="rounded-md border border-hx-neon/30 bg-hx-neon/[0.06] px-3 py-2.5"
          style={{ boxShadow: "0 0 18px -4px rgba(10,155,95,0.3)" }}
        >
          <div className="text-[9px] uppercase tracking-[0.18em] text-hx-neon font-bold">Total Semana</div>
          <div className="font-display font-bold text-lg text-hx-text tabular-nums leading-tight">
            {totals.hours}h <span className="text-hx-neon">· {totals.income} €</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* =================== CENTRO DE COMANDO (top row) =================== */

const CommandCenterRow = ({ entriesByCat, commandStore, roadmapSteps, onSaveText, editable }) => {
  return (
    <div className="rounded-lg border border-hx-neon/15 p-4 lg:p-5" style={{
      background: "linear-gradient(180deg, rgba(20,30,24,0.55) 0%, rgba(10,15,26,0.90) 100%)",
      boxShadow: "0 0 30px -16px rgba(10,155,95,0.4) inset",
    }} data-testid="fc-command-center">
      <div className="flex items-center justify-between mb-3.5 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.26em] text-hx-neon font-bold">Centro de Comando</div>
          <div className="text-[10px] text-hx-muted mt-0.5">Missão · Prioridade · Próximos passos · Auto-sincronizado</div>
        </div>
        <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted inline-flex items-center gap-2">
          <span className="relative flex w-1.5 h-1.5">
            <span className="absolute inline-flex w-full h-full rounded-full bg-hx-neon opacity-75 animate-ping" />
            <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-hx-neon" />
          </span>
          Atualização em tempo real
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-2.5 lg:gap-3">
        {COMMAND_CARDS.map((c) => {
          const stored = commandStore?.[c.key];
          const derived = deriveCommandValue(c.deriveFrom, entriesByCat, roadmapSteps);
          // Stored text wins for editable cards; for derived cards, fall back to derived
          const value = (c.editable && stored?.note) ? stored.note : (derived.value ?? c.placeholder);
          const sub = (c.editable && stored?.note) ? null : derived.sub;
          return (
            <div
              key={c.key}
              data-testid={`fc-cmd-${c.key}`}
              className="rounded-md border border-white/[0.10] bg-black/40 p-3 flex flex-col"
              style={{ minHeight: 108 }}
            >
              <div className="flex items-center gap-2">
                <c.Icon size={11} className="text-hx-neon" strokeWidth={1.7} />
                <span className="text-[9px] uppercase tracking-[0.2em] text-hx-muted font-semibold leading-tight">{c.label}</span>
              </div>
              <div className="mt-2 flex-1">
                {c.editable && editable ? (
                  <CommandEditableText
                    value={stored?.note || ""}
                    placeholder={c.placeholder}
                    onSave={(v) => onSaveText(c.key, v)}
                    testId={`fc-cmd-edit-${c.key}`}
                  />
                ) : (
                  <div className="font-display font-semibold text-[13px] lg:text-[14px] text-hx-text leading-snug line-clamp-3">{value}</div>
                )}
              </div>
              {sub && (
                <div className="mt-1.5 font-mono text-[10px] text-hx-neon/80 tabular-nums">{sub}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CommandEditableText = ({ value, onSave, placeholder, testId }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value || "");
  const ref = React.useRef(null);
  React.useEffect(() => setDraft(value || ""), [value]);
  React.useEffect(() => { if (editing) setTimeout(() => ref.current?.focus(), 30); }, [editing]);

  const commit = async () => {
    if (draft.trim() === (value || "").trim()) { setEditing(false); return; }
    try { await onSave(draft.trim()); } catch {/* toast handled by onSave */ }
    setEditing(false);
  };

  if (editing) {
    return (
      <textarea
        ref={ref}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commit(); } if (e.key === "Escape") { setDraft(value || ""); setEditing(false); } }}
        data-testid={testId}
        className="w-full bg-black/40 border border-hx-neon/30 rounded px-1.5 py-1 text-[13px] text-hx-text outline-none resize-none font-display font-semibold"
        rows={3}
      />
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      data-testid={testId}
      className={`w-full text-left font-display font-semibold text-[13px] lg:text-[14px] leading-snug line-clamp-3 hover:text-hx-neon transition-colors ${value ? "text-hx-text" : "text-hx-muted/60 italic"}`}
    >
      {value || placeholder}
    </button>
  );
};

/* =================== CONSTRUTOR DE LEGADO =================== */

const LegacyBuilder = ({ items, onCreate, onUpdate, onDelete, editable }) => {
  // Merge stored items with template skeletons so the user always sees the 8 canonical milestones
  const byLabel = React.useMemo(() => {
    const m = {};
    for (const it of (items || [])) m[it.label] = it;
    return m;
  }, [items]);

  const rows = React.useMemo(() => {
    const fromTemplates = LEGACY_TEMPLATES.map((t) => byLabel[t.label]
      ? { ...byLabel[t.label], _template: t }
      : { label: t.label, status: "active", progress: 0, _template: t, _stub: true }
    );
    const extras = (items || []).filter((it) => !LEGACY_TEMPLATES.find((t) => t.label === it.label));
    return [...fromTemplates, ...extras.map((e) => ({ ...e, _template: { Icon: Trophy } }))];
  }, [items, byLabel]);

  const completed = rows.filter((r) => r.status === "done").length;
  const total = rows.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  const toggleDone = async (row) => {
    if (!editable) return;
    if (row._stub) {
      await onCreate({ category: "legacy", label: row.label, status: "done", progress: 100 });
    } else {
      const nextStatus = row.status === "done" ? "active" : "done";
      await onUpdate(row.id, { status: nextStatus, progress: nextStatus === "done" ? 100 : (row.progress ?? 0) });
    }
  };

  return (
    <div
      className="rounded-lg border border-white/[0.10] p-5"
      style={{ background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)" }}
      data-testid="fc-legacy"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "rgba(10,155,95,0.1)", border: "1px solid rgba(10,155,95,0.3)" }}>
            <Trophy size={15} className="text-hx-neon" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-bold">Construtor de Legado</div>
            <div className="text-[10px] text-hx-muted mt-0.5">Marcos transformados em conquistas permanentes.</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display font-bold text-2xl text-hx-neon tabular-nums leading-none">{completed}/{total}</div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-hx-muted mt-1">Concluídos · {pct}%</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2.5">
        <AnimatePresence initial={false}>
          {rows.map((r, i) => {
            const done = r.status === "done";
            const TIcon = r._template?.Icon || Trophy;
            return (
              <motion.div
                key={r.id || `tpl:${r.label}`}
                layout
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                data-testid={`fc-legacy-${i}`}
                className={`rounded-md border p-3 flex items-start gap-3 group transition-all ${
                  done ? "border-hx-neon/40 bg-hx-neon/[0.06]" : "border-white/[0.10] bg-black/30 hover:border-hx-neon/20"
                }`}
                style={done ? { boxShadow: "0 0 18px -6px rgba(10,155,95,0.5) inset" } : undefined}
              >
                <button
                  onClick={() => toggleDone(r)}
                  disabled={!editable}
                  data-testid={`fc-legacy-toggle-${i}`}
                  className={`w-8 h-8 shrink-0 rounded-md flex items-center justify-center border transition-all ${
                    done
                      ? "border-hx-neon bg-hx-neon/15 text-hx-neon"
                      : "border-white/15 bg-black/40 text-hx-muted hover:border-hx-neon/40 hover:text-hx-neon"
                  }`}
                  title={done ? "Conquista alcançada" : (editable ? "Marcar como concluído" : "Login necessário")}
                  style={done ? { boxShadow: "0 0 10px rgba(10,155,95,0.6)" } : undefined}
                >
                  {done ? <Check size={14} strokeWidth={2.4} /> : <TIcon size={14} strokeWidth={1.7} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`text-[12px] font-display font-semibold leading-tight ${done ? "text-hx-text" : "text-hx-muted"}`}>{r.label}</div>
                  <div className={`text-[9px] uppercase tracking-[0.18em] mt-1 font-semibold ${done ? "text-hx-neon" : "text-hx-muted/70"}`}>
                    {done ? "Conquista" : (r.progress ? `${r.progress}% em curso` : "Em curso")}
                  </div>
                </div>
                {editable && !r._stub && r.id && (
                  <button
                    onClick={() => onDelete(r.id)}
                    data-testid={`fc-legacy-del-${i}`}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded text-hx-muted hover:text-red-300 hover:bg-red-500/10 transition-all shrink-0"
                    title="Remover"
                  >
                    <Trash2 size={11} strokeWidth={2} />
                  </button>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {editable && (
        <AddItemForm
          addLabel="Adicionar marco personalizado"
          testId="fc-legacy-add"
          fields={[
            { key: "label",    label: "Marco",         required: true, placeholder: "ex: Primeira Conferência Apresentada" },
            { key: "progress", label: "Progresso (%)", type: "number", placeholder: "0-100" },
            { key: "due_date", label: "Data prevista", type: "date" },
          ]}
          onSubmit={(d) => onCreate({ category: "legacy", ...d })}
        />
      )}
    </div>
  );
};

/* =================== DISTRIBUIÇÃO DE MISSÃO (por turno) =================== */

const MissionDistribution = ({ weeklyIncome }) => {
  // Per-shift example: 1 shift = 160 € distributed
  const perShift = 160;
  const dist = MISSION_BREAKDOWN.map((m) => ({
    ...m,
    perShift: Math.round(perShift * m.pct / 100),
    perWeek: Math.round((weeklyIncome || 0) * m.pct / 100),
  }));
  return (
    <div className="rounded-lg border border-white/[0.10] p-5" style={{
      background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)"
    }} data-testid="fc-mission-dist">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-semibold">Distribuição de Missão</div>
          <div className="text-[10px] text-hx-muted mt-0.5">Cada turno tem uma missão · Exemplo: turno 8h = 160 €</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.18em] text-hx-muted">Receita semanal estimada</div>
          <div className="font-display font-bold text-lg text-hx-neon tabular-nums">{weeklyIncome || 0} €</div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-2.5">
        {dist.map((m) => (
          <div key={m.key} className="rounded-md border border-white/[0.10] bg-black/30 p-3 flex flex-col gap-1.5" data-testid={`fc-mission-${m.key}`}>
            <div className="flex items-center gap-2">
              <m.Icon size={13} style={{ color: m.color }} strokeWidth={1.7} />
              <span className="text-[10px] uppercase tracking-[0.18em] text-hx-muted font-semibold">{m.label}</span>
              <span className="ml-auto font-mono text-[10px] text-hx-text tabular-nums">{m.pct}%</span>
            </div>
            <div className="font-display font-bold text-[15px] tabular-nums" style={{ color: m.color }}>{m.perShift} €<span className="text-[10px] text-hx-muted ml-1">/ turno</span></div>
            <div className="text-[10px] text-hx-muted tabular-nums">Semana: <span className="text-hx-text font-mono">{m.perWeek} €</span></div>
            <div className="mt-1 h-1 rounded-full bg-white/[0.03] overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${m.pct * 2.5}%`, background: m.color, boxShadow: `0 0 6px ${m.color}` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* =================== CALENDÁRIO OPERACIONAL (conectado) =================== */

const OperationalCalendar = ({ unlocked, weekEntries, entriesByCat, onSetDay }) => {
  const [cursor, setCursor] = React.useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selected, setSelected] = React.useState(() => new Date());

  // Build map: dayOfWeek → shiftId from weekEntries (private) or DEMO_WEEK (public)
  const weekShifts = React.useMemo(() => {
    if (unlocked) {
      const m = {};
      for (const e of (weekEntries || [])) {
        if (typeof e.label === "string" && e.label.startsWith("week:")) {
          const dayId = e.label.slice(5);
          if (e.note && SHIFT_BY_ID[e.note]) m[dayId] = e.note;
        }
      }
      return m;
    }
    return DEMO_WEEK;
  }, [unlocked, weekEntries]);

  // Aggregate ALL events keyed by ISO date for the visible window
  const eventsByDate = React.useMemo(() => {
    const map = {};
    const add = (iso, ev) => { if (!iso) return; (map[iso] ||= []).push(ev); };

    // Public demo OR private notes
    const notesSource = unlocked
      ? (entriesByCat?.notes || []).map((n) => ({ iso: n.due_date, label: n.label, color: "#0A9B5F", type: "note" }))
      : REMINDERS.map((r) => ({ iso: r.due_date, label: r.label, color: r.color, type: "note" }));
    for (const n of notesSource) add(n.iso, n);

    // Goals with due_date
    for (const g of (entriesByCat?.goals || [])) {
      if (g.due_date) add(g.due_date, { iso: g.due_date, label: g.label, color: "#0A9B5F", type: "goal" });
    }
    // Legacy with due_date
    for (const l of (entriesByCat?.legacy || [])) {
      if (l.due_date) add(l.due_date, { iso: l.due_date, label: l.label, color: "#A855F7", type: "milestone" });
    }
    // Family / driving / experiences / growth entries with due_date
    for (const cat of ["family", "driving", "experiences", "growth", "emergency", "hx", "faith"]) {
      for (const e of (entriesByCat?.[cat] || [])) {
        if (e.due_date) add(e.due_date, { iso: e.due_date, label: e.label, color: "#F97316", type: cat });
      }
    }
    return map;
  }, [unlocked, weekEntries, entriesByCat]);

  // Build 6-row × 7-col grid
  const grid = React.useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const firstDow = first.getDay(); // 0 Sun ... 6 Sat
    // offset so Monday=0
    const offset = (firstDow + 6) % 7;
    const start = new Date(year, month, 1 - offset);

    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [cursor]);

  const monthLabel = `${PT_MONTHS_FULL[cursor.getMonth()]} ${cursor.getFullYear()}`;

  const selectedIso = toIso(selected);
  const selectedEvents = eventsByDate[selectedIso] || [];
  const selectedDow = DOW_MAP[selected.getDay()];
  const selectedShift = SHIFT_BY_ID[weekShifts[selectedDow] || "off"];

  const today = new Date();
  const goPrev = () => { const d = new Date(cursor); d.setMonth(d.getMonth() - 1); setCursor(d); };
  const goNext = () => { const d = new Date(cursor); d.setMonth(d.getMonth() + 1); setCursor(d); };
  const goToday = () => { const d = new Date(); d.setDate(1); setCursor(d); setSelected(new Date()); };

  // Aggregate monthly totals
  const monthTotals = React.useMemo(() => {
    let hours = 0, income = 0, events = 0;
    const inMonth = grid.filter((d) => d.getMonth() === cursor.getMonth());
    for (const d of inMonth) {
      const dow = DOW_MAP[d.getDay()];
      const st = SHIFT_BY_ID[weekShifts[dow] || "off"];
      hours += st.hours; income += st.income;
      const evs = eventsByDate[toIso(d)] || [];
      events += evs.length;
    }
    return { hours, income, events };
  }, [grid, cursor, weekShifts, eventsByDate]);

  return (
    <div className="rounded-lg border border-white/[0.10]" style={{
      background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)"
    }} data-testid="fc-calendar">
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/[0.10] flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-semibold">Calendário Operacional</div>
          <div className="text-[10px] text-hx-muted mt-0.5">Turnos · Pagamentos · Reuniões · Marcos · Prazos</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={goPrev} data-testid="fc-cal-prev" className="w-8 h-8 rounded-md border border-white/[0.10] bg-black/30 hover:border-hx-neon/30 text-hx-muted hover:text-hx-text flex items-center justify-center transition-colors" aria-label="Mês anterior">‹</button>
          <button onClick={goToday} data-testid="fc-cal-today" className="px-3 h-8 rounded-md border border-white/[0.10] bg-black/30 hover:border-hx-neon/30 text-[10px] uppercase tracking-[0.18em] text-hx-muted hover:text-hx-neon transition-colors font-semibold">Hoje</button>
          <button onClick={goNext} data-testid="fc-cal-next" className="w-8 h-8 rounded-md border border-white/[0.10] bg-black/30 hover:border-hx-neon/30 text-hx-muted hover:text-hx-text flex items-center justify-center transition-colors" aria-label="Próximo mês">›</button>
          <div className="ml-3 font-display font-bold text-base text-hx-text capitalize min-w-[180px] text-right" data-testid="fc-cal-label">{monthLabel}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px]">
        {/* Calendar grid */}
        <div className="p-4 lg:p-5">
          {/* DOW header */}
          <div className="grid grid-cols-7 gap-1 mb-1.5">
            {WEEK_START_ORDER.map((d) => (
              <div key={d} className="text-center text-[9px] uppercase tracking-[0.22em] text-hx-muted font-semibold py-1.5">{PT_DOW_SHORT[d]}</div>
            ))}
          </div>
          {/* Cells */}
          <div className="grid grid-cols-7 gap-1">
            {grid.map((d, i) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = isSameDay(d, today);
              const isSelected = isSameDay(d, selected);
              const dow = DOW_MAP[d.getDay()];
              const sid = weekShifts[dow] || "off";
              const st = SHIFT_BY_ID[sid];
              const iso = toIso(d);
              const evs = eventsByDate[iso] || [];

              return (
                <button
                  key={i}
                  onClick={() => setSelected(d)}
                  data-testid={`fc-cal-cell-${iso}`}
                  className={`relative rounded-md border text-left p-1.5 transition-all min-h-[68px] flex flex-col group ${
                    isSelected
                      ? "border-hx-neon bg-hx-neon/[0.08]"
                      : isToday
                        ? "border-hx-neon/40 bg-black/40"
                        : "border-white/[0.10] bg-black/20 hover:border-hx-neon/20"
                  } ${inMonth ? "" : "opacity-35"}`}
                  style={isSelected ? { boxShadow: "0 0 18px -4px rgba(10,155,95,0.5) inset" } : (sid !== "off" ? { boxShadow: `inset 3px 0 0 0 ${st.color}` } : undefined)}
                >
                  <div className="flex items-start justify-between">
                    <span className={`font-mono text-[11px] tabular-nums ${isToday ? "text-hx-neon font-bold" : "text-hx-text"}`}>
                      {d.getDate()}
                    </span>
                    {sid !== "off" && (
                      <st.Icon size={9} style={{ color: st.color }} strokeWidth={1.8} />
                    )}
                  </div>
                  {sid !== "off" && (
                    <div className="mt-0.5 text-[8px] uppercase tracking-[0.12em] font-semibold leading-none" style={{ color: st.color }}>
                      {st.label}
                    </div>
                  )}
                  {/* Event dots */}
                  <div className="mt-auto flex flex-wrap items-center gap-0.5 pt-1">
                    {evs.slice(0, 4).map((e, j) => (
                      <span key={j} className="w-1.5 h-1.5 rounded-full" style={{ background: e.color, boxShadow: `0 0 4px ${e.color}` }} />
                    ))}
                    {evs.length > 4 && <span className="text-[8px] text-hx-muted ml-0.5">+{evs.length - 4}</span>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Month totals strip */}
          <div className="mt-4 grid grid-cols-3 gap-2.5">
            <div className="rounded-md border border-white/[0.10] bg-black/30 px-3 py-2">
              <div className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">Horas no Mês</div>
              <div className="font-display font-bold text-lg text-hx-text tabular-nums">{monthTotals.hours}h</div>
            </div>
            <div className="rounded-md border border-hx-neon/30 bg-hx-neon/[0.06] px-3 py-2" style={{ boxShadow: "0 0 18px -8px rgba(10,155,95,0.4) inset" }}>
              <div className="text-[9px] uppercase tracking-[0.18em] text-hx-neon">Receita Estimada</div>
              <div className="font-display font-bold text-lg text-hx-neon tabular-nums">{new Intl.NumberFormat("de-DE").format(monthTotals.income)} €</div>
            </div>
            <div className="rounded-md border border-white/[0.10] bg-black/30 px-3 py-2">
              <div className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">Eventos</div>
              <div className="font-display font-bold text-lg text-hx-text tabular-nums">{monthTotals.events}</div>
            </div>
          </div>
        </div>

        {/* Selected day side panel */}
        <div className="border-t xl:border-t-0 xl:border-l border-white/[0.10] p-4 lg:p-5 bg-black/30">
          <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted">Dia selecionado</div>
          <div className="mt-1 font-display font-bold text-xl text-hx-text">
            {selected.getDate()} {PT_MONTHS_FULL[selected.getMonth()]}
          </div>
          <div className="text-[11px] text-hx-muted capitalize">{PT_DOW_SHORT[selected.getDay()]} · {selected.getFullYear()}</div>

          {/* Shift block */}
          <div className="mt-4 rounded-md border border-white/[0.10] bg-black/40 p-3 flex items-center gap-3" data-testid="fc-cal-selected-shift">
            <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{
              background: `${selectedShift.color}22`,
              border: `1px solid ${selectedShift.color}66`,
            }}>
              <selectedShift.Icon size={15} style={{ color: selectedShift.color }} strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">Turno</div>
              <div className="font-display font-semibold text-[13px]" style={{ color: selectedShift.color }}>{selectedShift.label}</div>
              <div className="text-[10px] font-mono text-hx-muted">{selectedShift.range}</div>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-[0.18em] text-hx-muted">Receita</div>
              <div className="font-display font-bold text-[13px] tabular-nums" style={{ color: selectedShift.color }}>{selectedShift.income} €</div>
            </div>
          </div>

          {/* Quick shift switcher (private) */}
          {unlocked && (
            <div className="mt-2.5 grid grid-cols-5 gap-1.5">
              {SHIFT_TYPES.map((opt) => {
                const isActive = (weekShifts[selectedDow] || "off") === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => onSetDay(selectedDow, opt.id)}
                    data-testid={`fc-cal-shift-${selectedDow}-${opt.id}`}
                    title={`${opt.label} · ${opt.range}`}
                    className={`h-7 rounded text-[9px] uppercase tracking-[0.14em] font-semibold transition-all border ${
                      isActive ? "scale-105 text-hx-bg" : "text-hx-muted hover:text-hx-text"
                    }`}
                    style={{
                      borderColor: isActive ? opt.color : "rgba(255,255,255,0.08)",
                      background: isActive ? opt.color : "transparent",
                      boxShadow: isActive ? `0 0 10px ${opt.color}99` : undefined,
                    }}
                  >
                    {opt.label.slice(0, 4)}
                  </button>
                );
              })}
            </div>
          )}

          {/* Events list */}
          <div className="mt-4">
            <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted mb-2">Eventos · {selectedEvents.length}</div>
            {selectedEvents.length === 0 ? (
              <div className="text-[12px] text-hx-muted/70 italic">Sem eventos neste dia.</div>
            ) : (
              <ul className="space-y-1.5">
                {selectedEvents.map((e, i) => (
                  <li key={i} className="flex items-start gap-2 text-[12px] rounded-md border border-white/[0.10] bg-black/40 px-2.5 py-2" data-testid={`fc-cal-event-${i}`}>
                    <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: e.color, boxShadow: `0 0 6px ${e.color}` }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-hx-text leading-tight">{e.label}</div>
                      <div className="text-[9px] uppercase tracking-[0.16em] text-hx-muted mt-0.5">{e.type}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Legend */}
          <div className="mt-5 pt-3 border-t border-white/[0.10]">
            <div className="text-[9px] uppercase tracking-[0.2em] text-hx-muted mb-1.5">Legenda</div>
            <div className="grid grid-cols-2 gap-y-1 gap-x-2 text-[10px] text-hx-muted">
              <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Lembrete</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Objetivo</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-purple-400" /> Marco</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-orange-400" /> Família/Geral</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FooterMantra = React.memo(function FooterMantra() {
  return (
  <footer className="border-t border-white/[0.10] bg-black/40 px-5 lg:px-8 py-5" data-testid="fc-mantra">
    <div className="flex items-center justify-between gap-5 flex-wrap">
      <div className="flex items-center gap-x-5 gap-y-1.5 flex-wrap font-mono text-[10px] uppercase tracking-[0.22em] text-hx-muted">
        <span className="inline-flex items-center gap-2"><Heart        size={11} className="text-hx-neon" /> Honra a Fé.</span>
        <span className="inline-flex items-center gap-2"><ShieldCheck  size={11} className="text-hx-neon" /> Protege a Família.</span>
        <span className="inline-flex items-center gap-2"><PiggyBank    size={11} className="text-hx-neon" /> Constrói Segurança.</span>
        <span className="inline-flex items-center gap-2"><GraduationCap size={11} className="text-hx-neon" /> Desenvolve Competências.</span>
        <span className="inline-flex items-center gap-2"><Plane        size={11} className="text-hx-neon" /> Cria Experiências.</span>
        <span className="inline-flex items-center gap-2"><TrendingUp   size={11} className="text-hx-neon" /> Financia Inovação.</span>
        <span className="inline-flex items-center gap-2"><Briefcase    size={11} className="text-hx-neon" /> Faz Crescer a HX Ecosystems.</span>
        <span className="inline-flex items-center gap-2"><Target       size={11} className="text-hx-neon" /> Constrói um Legado.</span>
      </div>
      <div className="font-display font-bold text-hx-neon text-base">HX</div>
    </div>
  </footer>
  );
});

/* =================== CATEGORY SHEET DEFINITIONS =================== */
// Each private nav item maps to a CategorySheet with curated columns + description.

/* Reports & Settings — light placeholder views */
const ReportsView = ({ state }) => {
  const counts = {};
  let totalAmount = 0, totalTarget = 0, totalMonthly = 0;
  for (const e of (state?.entries || [])) {
    counts[e.category] = (counts[e.category] || 0) + 1;
    totalAmount  += e.amount  || 0;
    totalTarget  += e.target  || 0;
    totalMonthly += e.monthly || 0;
  }
  const rows = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return (
    <div data-testid="sheet-reports">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-md hx-metal flex items-center justify-center"><BarChart3 size={18} className="text-hx-neon" strokeWidth={1.6} /></div>
        <div>
          <h1 className="font-display font-bold text-2xl lg:text-3xl tracking-tight">Relatórios & Analítica</h1>
          <p className="mt-1 text-[13px] text-hx-muted">Visão agregada de todos os registos financeiros e operacionais.</p>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Total de Registos", value: state?.entries?.length || 0, suffix: "" },
          { label: "Soma de Valores",   value: totalAmount, suffix: "€" },
          { label: "Soma de Alvos",     value: totalTarget, suffix: "€" },
          { label: "Mensal Total",      value: totalMonthly, suffix: "€" },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-white/[0.10] p-4" style={{ background: "rgba(20,26,34,0.6)" }}>
            <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted">{s.label}</div>
            <div className="mt-2 font-display font-bold text-2xl text-hx-neon tabular-nums">
              {new Intl.NumberFormat("de-DE").format(s.value)} <span className="text-[12px] text-hx-muted">{s.suffix}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-white/[0.10] p-5" style={{ background: "rgba(20,26,34,0.55)" }}>
        <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-semibold mb-4">Registos por Categoria</div>
        {rows.length === 0
          ? <div className="text-[12px] text-hx-muted/70 italic">Sem dados ainda. Comece em qualquer categoria do menu lateral.</div>
          : <ul className="space-y-2">
              {rows.map(([cat, n]) => (
                <li key={cat} className="flex items-center justify-between text-[12px]">
                  <span className="text-hx-text capitalize">{cat}</span>
                  <span className="font-mono text-hx-neon tabular-nums">{n}</span>
                </li>
              ))}
            </ul>}
      </div>
    </div>
  );
};

const SettingsView = ({ onLock }) => (
  <div data-testid="sheet-settings" className="max-w-2xl">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-11 h-11 rounded-md hx-metal flex items-center justify-center"><Settings size={18} className="text-hx-neon" strokeWidth={1.6} /></div>
      <div>
        <h1 className="font-display font-bold text-2xl lg:text-3xl tracking-tight">Definições</h1>
        <p className="mt-1 text-[13px] text-hx-muted">Sessão privada e preferências.</p>
      </div>
    </div>
    <div className="rounded-lg border border-white/[0.10] p-5 space-y-4" style={{ background: "rgba(20,26,34,0.55)" }}>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-hx-muted font-semibold mb-1">Estado da sessão</div>
        <div className="text-[12px] text-hx-text">Modo privado ativo. Token guardado localmente no dispositivo.</div>
      </div>
      <hr className="border-white/[0.10]" />
      <button
        onClick={onLock}
        data-testid="settings-lock"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 transition-colors text-[10px] uppercase tracking-[0.2em] font-semibold"
      >
        <Lock size={11} strokeWidth={2.2} /> Bloquear painel agora
      </button>
    </div>
  </div>
);

/* Public stub views — Philosophy, System Overview, etc. (reuse dashboard for Home and Demo Dashboard) */
const PublicStubView = ({ title, description }) => (
  <div className="max-w-3xl">
    <h1 className="font-display font-bold text-2xl lg:text-3xl tracking-tight">{title}</h1>
    <p className="mt-3 text-[13px] text-hx-muted leading-relaxed">{description}</p>
    <div className="mt-8 rounded-lg border border-white/[0.10] p-5 text-[12px] text-hx-muted italic" style={{ background: "rgba(20,26,34,0.55)" }}>
      Esta secção está disponível em modo demonstração. Conteúdo detalhado a ser publicado em breve.
    </div>
  </div>
);

/* =================== MAIN DASHBOARD =================== */
export const FinanceCommandDashboard = ({ unlocked, onUnlockClick, onLock, state }) => {
  const [active, setActive] = React.useState("dashboard");
  const editable = !!unlocked && !!state;

  // Build view models from real state OR demo fallback — granular deps to avoid re-renders
  const kpisStore   = state?.kpis;
  const entriesByCat = state?.entriesByCat;
  const kpisList     = React.useMemo(() => buildKpiList(kpisStore || {}, entriesByCat || {}), [kpisStore, entriesByCat]);
  const categoryList = React.useMemo(() => buildCategoryCards(entriesByCat || {}, kpisStore || {}), [entriesByCat, kpisStore]);
  const reminders    = React.useMemo(() => buildReminders(entriesByCat?.notes || []), [entriesByCat]);
  const goals        = React.useMemo(() => buildGoals(entriesByCat?.goals || []), [entriesByCat]);
  const investments  = React.useMemo(() => buildInvestments(entriesByCat?.hx || []), [entriesByCat]);
  const budgetSlices = React.useMemo(() => buildBudgetSlices(entriesByCat || {}, !!unlocked), [entriesByCat, unlocked]);
  const shifts       = React.useMemo(() => {
    const nonWeek = (entriesByCat?.shifts || []).filter((e) => !(typeof e.label === "string" && e.label.startsWith("week:")));
    return buildShifts(nonWeek);
  }, [entriesByCat]);

  // Save handlers — when user edits a KPI/category value, persist to the right place
  const saveKpi = (key) => (val) => state.upsert({ category: "kpis", label: key, amount: val });
  // Map category cards to KPI keys when applicable so dashboard auto-syncs
  const CARD_KPI_KEY = {
    monthly_income: "monthly_income",
    total_expenses: "total_expenses",
    emergency: "emergency_reserve",
    hx: "hx_invested",
    faith: null, family: null, growth: null, experiences: null,
  };
  const saveCategoryEntry = (cat) => async (val) => {
    const kpiKey = CARD_KPI_KEY[cat];
    if (kpiKey) {
      // Sync the corresponding top KPI so percentages auto-recompute
      await state.upsert({ category: "kpis", label: kpiKey, amount: val });
    } else {
      // For cards without a KPI mirror (faith, family, growth, experiences),
      // store as a single "primary" entry in that category
      await state.upsert({ category: cat, label: "primary", amount: val });
    }
  };
  const addReminder    = (data) => state.create({ category: "notes", ...data });
  const deleteReminder = (id)   => state.remove(id);
  const addGoal        = (data) => state.create({ category: "goals", ...data });
  const updateGoal     = (id, patch) => state.update(id, patch);
  const deleteGoal     = (id)   => state.remove(id);
  const addInvestment  = (data) => state.create({ category: "hx", ...data });
  const updateInvest   = (id, patch) => state.update(id, patch);
  const deleteInvest   = (id)   => state.remove(id);
  const addShift = (data) => state.create({
    category: "shifts",
    label: data.label,
    note: data.range,
    amount: data.count,
    target: data.hours,
    monthly: data.income,
  });
  const deleteShift = (id) => state.remove(id);

  // Weekly schedule: filter shift entries by `week:` label prefix
  const weekEntries = React.useMemo(
    () => (entriesByCat?.shifts || []).filter((e) => typeof e.label === "string" && e.label.startsWith("week:")),
    [entriesByCat],
  );
  const setWeekDay = (dayId, shiftId) => {
    if (!editable) return;
    const st = SHIFT_BY_ID[shiftId] || SHIFT_BY_ID.off;
    return state.upsert({
      category: "shifts",
      label: `week:${dayId}`,
      note: shiftId,
      amount: st.hours,
      monthly: st.income,
    });
  };

  /* ---- Dynamic roadmap (auto-sync ↔ Command Center) ----
   * Merge static ROADMAP defaults with overrides stored in entriesByCat.roadmap.
   * Each override: { label: "step-{n}", status: "done"|"next"|"active", note?: "..." }
   */
  const roadmapSteps = React.useMemo(() => {
    const overrides = {};
    for (const e of (entriesByCat?.roadmap || [])) {
      const m = /^step-(\d+)$/.exec(e.label || "");
      if (m) overrides[Number(m[1])] = e;
    }
    return ROADMAP.map((r) => {
      const ov = overrides[r.n];
      if (!ov) return r;
      const ovStatus = ov.note;  // we store roadmap state in `note` (done|next|active)
      const isDone = ovStatus === "done";
      const isNext = ovStatus === "next";
      return {
        ...r,
        done: isDone,
        next: isNext,
        status: isDone ? "Concluído" : isNext ? "Próxima Prioridade" : (r.status || "Em Curso"),
      };
    });
  }, [entriesByCat]);

  const toggleRoadmapDone = async (n) => {
    if (!editable) return;
    const current = roadmapSteps.find((r) => r.n === n);
    const nextStatus = current?.done ? "active" : "done";
    await state.upsert({ category: "roadmap", label: `step-${n}`, note: nextStatus, amount: nextStatus === "done" ? 100 : 0 });
  };
  const setRoadmapNext = async (n) => {
    if (!editable) return;
    // Clear any previously-marked "next" override, then mark this one
    for (const e of (entriesByCat?.roadmap || [])) {
      if (e.note === "next" && e.label !== `step-${n}`) {
        await state.update(e.id, { note: "active" });
      }
    }
    await state.upsert({ category: "roadmap", label: `step-${n}`, note: "next" });
  };

  // Command center store (category="command", indexed by label)
  const commandStore = React.useMemo(() => {
    const m = {};
    for (const e of (entriesByCat?.command || [])) m[e.label] = e;
    return m;
  }, [entriesByCat]);

  // Legacy store
  const legacyItems = entriesByCat?.legacy || [];

  // Weekly schedule weekly income
  const weekTotals = React.useMemo(() => {
    const SHIFT_INCOMES = { morning: 160, day: 160, evening: 160, night: 160, off: 0 };
    let income = 0;
    if (editable) {
      for (const e of (weekEntries || [])) {
        if (typeof e.label === "string" && e.label.startsWith("week:") && e.note) {
          income += SHIFT_INCOMES[e.note] ?? 0;
        }
      }
    } else {
      const DEMO = { mon: "morning", tue: "morning", wed: "day", thu: "morning", fri: "night", sat: "evening", sun: "off" };
      for (const d of ["mon","tue","wed","thu","fri","sat","sun"]) income += SHIFT_INCOMES[DEMO[d]] ?? 0;
    }
    return { income };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekEntries, editable]);

  const saveCommandText = (key, value) => state.upsert({ category: "command", label: key, note: value });

  return (
    <div className="min-h-screen bg-hx-bg text-hx-text flex" data-testid="finance-command">
      <Sidebar active={active} setActive={setActive} unlocked={unlocked} />

      <div className="flex-1 min-w-0 flex flex-col">
        <TopHeader unlocked={unlocked} onUnlockClick={onUnlockClick} onLock={onLock} />

        {!unlocked && (
          <div className="px-5 lg:px-8 pt-4">
            <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.08] px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid="fc-demo-banner">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-amber-200/90 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                Dados Demo — Apenas para fins de demonstração
              </div>
              <button onClick={onUnlockClick} data-testid="fc-unlock-btn" className="text-[10px] uppercase tracking-[0.2em] text-hx-neon hover:text-hx-text transition-colors font-semibold inline-flex items-center gap-1.5">
                <Lock size={10} strokeWidth={2.2} /> Desbloquear modo privado
              </button>
            </div>
          </div>
        )}

        {editable && (
          <div className="px-5 lg:px-8 pt-4">
            <div className="rounded-md border border-hx-neon/25 bg-hx-neon/[0.06] px-4 py-2 text-[10px] uppercase tracking-[0.22em] text-hx-neon font-medium flex items-center gap-2" data-testid="fc-edit-banner" style={{ boxShadow: "0 0 18px -6px rgba(10,155,95,0.4) inset" }}>
              <span className="relative flex w-1.5 h-1.5">
                <span className="absolute inline-flex w-full h-full rounded-full bg-hx-neon opacity-75 animate-ping" />
                <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-hx-neon" />
              </span>
              Edição direta ativa — clique em qualquer valor ou texto para editar
            </div>
          </div>
        )}

        <main className="flex-1 px-5 lg:px-8 py-6 lg:py-8 space-y-5 lg:space-y-6">
          {/* Active view router */}
          {(() => {
            // Public views (only used when not unlocked OR when explicitly browsing)
            if (!unlocked) {
              if (active === "philosophy") {
                return <PublicStubView title="Filosofia" description="HX Finance Command opera sob seis princípios: Fé Primeiro, Família Protegida, Reserva Antes de Risco, Skill Compounding, Projeto Tem Orçamento, Tempo é Capital." />;
              }
              if (active === "overview") {
                return <PublicStubView title="Visão do Sistema" description="Plataforma de comando pessoal para fé, família, formação, mobilidade e projetos HX Ecosystems. Modo público mostra dados ilustrativos; modo privado desbloqueia controlo total." />;
              }
              if (active === "goals-ex") {
                return <PublicStubView title="Objetivos (Exemplo)" description="Exemplos de objetivos quantificáveis: Reserva de Emergência 6 meses, Alemão B1→B2, Início da Ausbildung, Carta de Condução, Lançamento HX Smart Point." />;
              }
              if (active === "roadmap-ex") {
                return <PublicStubView title="Roadmap (Exemplo)" description="Dez missões ordenadas: Fé → Estabilidade Familiar → Reserva → Alemão → Ausbildung → Crescimento Profissional → Carta → HX → Expansão → Legado." />;
              }
              if (active === "about") {
                return <PublicStubView title="Sobre HX Ecosystems" description="HX Ecosystems é uma plataforma europeia de infraestrutura inteligente para mobilidade, energia, BESS e orquestração operacional. HX Finance Command é a divisão privada de planeamento de vida, família e projetos." />;
              }
              // Default (home / demo dashboard) — full demo dashboard
            }

            // Private routed views
            if (unlocked) {
              if (active === "ausbildung") {
                const sheet = ausbildungSheet;
                return (
                  <CategorySheet
                    title={sheet.title}
                    icon={sheet.icon}
                    description={sheet.description}
                    category="growth"
                    columns={sheet.columns}
                    entries={state?.entriesByCat?.growth || []}
                    onCreate={state.create}
                    onUpdate={state.update}
                    onDelete={state.remove}
                  />
                );
              }
              if (SHEET_MAP[active]) {
                const sheet = SHEET_MAP[active];
                // For "shifts", hide the internal `week:*` entries — those are managed by the WeeklyShiftSchedule
                const rawEntries = state?.entriesByCat?.[active] || [];
                const entries = active === "shifts"
                  ? rawEntries.filter((e) => !(typeof e.label === "string" && e.label.startsWith("week:")))
                  : rawEntries;
                return (
                  <CategorySheet
                    title={sheet.title}
                    icon={sheet.icon}
                    description={sheet.description}
                    category={active}
                    columns={sheet.columns}
                    entries={entries}
                    onCreate={state.create}
                    onUpdate={state.update}
                    onDelete={state.remove}
                  />
                );
              }
              if (active === "reports") return <ReportsView state={state} />;
              if (active === "settings") return <SettingsView onLock={onLock} />;
              // dashboard / home / demo → render the full dashboard below
            }

            // Default = full operational dashboard
            return (
              <>
                {/* 1. CENTRO DE COMANDO — Missão, Prioridade, Próximos */}
                <CommandCenterRow
                  entriesByCat={{ ...(entriesByCat || {}), legacy: legacyItems }}
                  commandStore={commandStore}
                  roadmapSteps={roadmapSteps}
                  onSaveText={saveCommandText}
                  editable={editable}
                />

                {/* 2. KPIs */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
                  {kpisList.map((k) => (
                    <KPICard key={k.label} kpi={k} editable={editable} onSave={editable ? saveKpi(k.key) : undefined} />
                  ))}
                </div>

                {/* 3. SISTEMA DE VIDA — Categories + side stack (Roadmap + Goals) */}
                <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr_320px] gap-4 lg:gap-5">
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 xl:col-span-2">
                    {categoryList.map((c) => (
                      <CategoryCard key={c.key} c={c} editable={editable} onSave={editable ? saveCategoryEntry(c.key) : undefined} />
                    ))}
                  </div>
                  <div className="space-y-5">
                    <RoadmapPanel steps={roadmapSteps} onToggleDone={toggleRoadmapDone} onSetNext={setRoadmapNext} editable={editable} />
                    <TopGoalsPanel items={goals} onAdd={addGoal} onUpdate={updateGoal} onDelete={deleteGoal} editable={editable} />
                  </div>
                </div>

                {/* 4. CASH FLOW + BUDGET */}
                <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-4 lg:gap-5">
                  <CashFlowChart />
                  <BudgetDonut slices={budgetSlices} totalLabel="Alocação Total" />
                </div>

                {/* 5. ESCALA DE TRABALHO + DISTRIBUIÇÃO DE MISSÃO */}
                <WeeklyShiftSchedule unlocked={unlocked} weekEntries={weekEntries} onSetDay={setWeekDay} />
                <MissionDistribution weeklyIncome={weekTotals.income} />

                {/* 5b. CALENDÁRIO OPERACIONAL — visão mensal conectada */}
                <OperationalCalendar
                  unlocked={unlocked}
                  weekEntries={weekEntries}
                  entriesByCat={{ ...(entriesByCat || {}), legacy: legacyItems }}
                  onSetDay={setWeekDay}
                />

                {/* 6. LEMBRETES + INVESTIMENTOS HX */}
                <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.2fr] gap-4 lg:gap-5">
                  <RemindersPanel items={reminders} onAdd={addReminder} onDelete={deleteReminder} editable={editable} />
                  <HXInvestmentsPanel items={investments} onAdd={addInvestment} onUpdate={updateInvest} onDelete={deleteInvest} editable={editable} />
                </div>

                {/* 7. CONSTRUTOR DE LEGADO */}
                <LegacyBuilder
                  items={legacyItems}
                  onCreate={(d) => state.create(d)}
                  onUpdate={(id, p) => state.update(id, p)}
                  onDelete={(id) => state.remove(id)}
                  editable={editable}
                />
              </>
            );
          })()}
        </main>

        <FooterMantra />
      </div>
    </div>
  );
};
